import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Page } from "@/components/Layout";
import Seo from "@/components/Seo";
import { useCart } from "@/lib/useCart";
import { useCurrency } from "@/lib/currency";
import { ShoppingBag, ArrowRight, Trash, Plus, Minus, CaretLeft } from "@phosphor-icons/react";

export default function CartPage() {
  const { cart, total, add: addCart, dec, remove } = useCart();
  const { format } = useCurrency();
  const navigate = useNavigate();

  const grand = total;

  return (
    <Page>
      <Seo title="Your Cart" description="Review the items in your MindDhara shop cart." />
      <section className="md-section pt-8 md:pt-12">
        <div className="md-container">
          <button onClick={() => navigate(-1)} className="pd-back" data-testid="cart-back">
            <CaretLeft size={14} weight="bold" /> Continue shopping
          </button>

          <div className="flex items-end justify-between flex-wrap gap-3 mt-5 mb-7">
            <div>
              <div className="md-label-eyebrow mb-2">Cart</div>
              <h1 className="text-3xl md:text-4xl font-serif">Your cart{cart.length > 0 ? ` · ${cart.length} item${cart.length === 1 ? "" : "s"}` : ""}</h1>
            </div>
          </div>

          {cart.length === 0 ? (
            <div className="md-card text-center py-16" data-testid="cart-empty">
              <ShoppingBag size={48} weight="duotone" className="mx-auto" style={{ color: "var(--md-text-muted)" }} />
              <h3 className="text-xl font-serif mt-4">Your cart is empty</h3>
              <p className="text-sm mt-2" style={{ color: "var(--md-text-soft)" }}>Browse the shop and add a few wellbeing tools.</p>
              <Link to="/shop" className="btn-primary mt-6" data-testid="cart-empty-shop">Browse the Shop <ArrowRight size={14} weight="bold" /></Link>
            </div>
          ) : (
            <div className="cart-layout">
              <div className="cart-rows" data-testid="cart-rows">
                {cart.map((c) => (
                  <div key={c.id} className="cart-row" data-testid={`cart-page-row-${c.id}`}>
                    <Link to={`/shop/${c.id}`} className="cart-row-thumb">
                      {c.image_url ? <img src={c.image_url} alt={c.name} /> : <div className="cart-row-thumb-ph" />}
                    </Link>
                    <div className="cart-row-meta">
                      <Link to={`/shop/${c.id}`} className="cart-row-name">{c.name}</Link>
                      <div className="cart-row-price">{c.price > 0 ? format(c.price) : "Free"}</div>
                    </div>
                    <div className="cart-row-qty">
                      <button type="button" onClick={() => dec(c.id)} aria-label="Decrease" data-testid={`cart-dec-${c.id}`}><Minus size={12} weight="bold" /></button>
                      <span data-testid={`cart-qty-${c.id}`}>{c.qty}</span>
                      <button type="button" onClick={() => addCart(c)} aria-label="Increase" data-testid={`cart-inc-${c.id}`}><Plus size={12} weight="bold" /></button>
                    </div>
                    <div className="cart-row-line">{format(c.price * c.qty)}</div>
                    <button type="button" onClick={() => remove(c.id)} className="cart-row-remove" aria-label="Remove" data-testid={`cart-remove-${c.id}`}>
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>

              <aside className="cart-summary" data-testid="cart-summary">
                <h3 className="font-serif text-xl mb-4">Order summary</h3>
                <div className="cart-summary-row"><span>Subtotal</span><strong>{format(total)}</strong></div>
                <div className="cart-summary-row">
                  <span>Delivery</span>
                  <strong><em style={{ color: "var(--md-primary)" }}>Free (digital)</em></strong>
                </div>
                <p className="text-xs mt-1" style={{ color: "var(--md-text-muted)" }}>
                  Instant download via email after payment.
                </p>
                <div className="cart-summary-divider" />
                <div className="cart-summary-row cart-summary-grand"><span>Total</span><strong>{format(grand)}</strong></div>
                <button onClick={() => navigate("/checkout")} className="btn-primary w-full justify-center mt-5" data-testid="cart-checkout-btn">
                  Proceed to Checkout <ArrowRight size={14} weight="bold" />
                </button>
                <p className="text-xs text-center mt-3" style={{ color: "var(--md-text-muted)" }}>Secure payment by Razorpay · Sign in required.</p>
              </aside>
            </div>
          )}
        </div>
      </section>
    </Page>
  );
}
